/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, type MouseEvent, Suspense } from 'react';
import { motion, MotionConfig, AnimatePresence } from 'motion/react';
import SmoothScroll from './components/SmoothScroll';



import { useI18n } from './i18n';
import AntigravityLauncher from './components/AntigravityLauncher';
import { useScrolled } from './hooks/useScrolled';
import { ErrorBoundary } from './components/ErrorBoundary';




import {
  type AppView,
  type LandingView,
  landingViewToPath,
  routeMetaByView,
  isLandingView,
  mapPathToView,
} from './routes';


const Home = React.lazy(() => import('./pages/Home'));
const Expertise = React.lazy(() => import('./pages/Expertise'));
const Engagement = React.lazy(() => import('./pages/Engagement'));
const ITServices = React.lazy(() => import('./pages/ITServices'));
const Contact = React.lazy(() => import('./pages/Contact'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies'));
const Solutions = React.lazy(() => import('./pages/Solutions'));
const Blog = React.lazy(() => import('./pages/Blog'));
const Matjaar = React.lazy(() => import('./pages/Matjaar'));
const NotFound = React.lazy(() => import('./pages/NotFound'));




function getPathForView(view: LandingView) {
  return landingViewToPath.get(view) || '/';
}

import { blogPosts } from './data/blogPosts';


export interface AppProps {
  initialPath?: string;
}

export default function App({ initialPath }: AppProps = {}) {
  const { t, lang, setLang } = useI18n();
  const isAdminHost = false;
  
  
  const scrolled = useScrolled(60);
  
  
  const navCollapseRef = useRef<HTMLDivElement>(null);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  
  const [view, setView] = useState<AppView>(() => {
    const path = typeof window === 'undefined' ? (initialPath || '/') : window.location.pathname;
    
    if (path.startsWith('/blog/')) return 'landing_blog';
    
    return mapPathToView(path);
  });
  const [selectedPostId, setSelectedPostId] = useState<number | null>(() => {
    const path = typeof window === 'undefined' ? (initialPath || '/') : window.location.pathname;
    if (path.startsWith('/blog/')) {
      const idStr = path.split('/')[2];
      return idStr ? parseInt(idStr, 10) : null;
    }
    
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const postVal = params.get('post');
      return postVal ? parseInt(postVal, 10) : null;
    }
    return null;
  });
  const [cookieConsent, setCookieConsent] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consent = localStorage.getItem('vye_cookie_consent') === 'true';
      if (!consent) {
        setCookieConsent(false);
      }
    }
  }, []);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success'>('idle');
  
  

  const acceptCookies = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('vye_cookie_consent', 'true');
    }
    setCookieConsent(true);
  };
  
  // SEO Updates
  useEffect(() => {
    if (typeof document === 'undefined') return;

    let title = lang === 'ar'
      ? 'VYE: شركة الحلول التقنية وتطوير البرمجيات الرائدة في مصر'
      : "VYE: Best IT Solutions Company & Software Development in Egypt";
    let metaDesc = lang === 'ar'
      ? "تقدم VYE أحدث حلول تكنولوجيا المعلومات للشركات في مصر، وتتخصص في خدمات السحابة وتطوير البرمجيات المخصصة والأمن السيبراني والتحول الرقمي."
      : "VYE provides next-gen enterprise IT solutions in Egypt, specializing in cloud services, custom software development, cybersecurity, and digital transformation.";
    
    if (isLandingView(view)) {
      const routeMeta = routeMetaByView.get(view);
      if (routeMeta) {
        title = lang === 'ar' ? (routeMeta.titleAr || routeMeta.title) : routeMeta.title;
        metaDesc = lang === 'ar' ? (routeMeta.descriptionAr || routeMeta.description) : routeMeta.description;
      }
    }

    if (view === 'landing_blog' && selectedPostId !== null) {
      const post = blogPosts.find(p => p.id === selectedPostId);
      if (post) {
        title = lang === 'ar' ? `${post.titleAr} - مدونة VYE` : `${post.titleEn} - VYE Blog`;
        metaDesc = lang === 'ar' ? post.summaryAr : post.summaryEn;
      }
    }

    document.title = title;
    
    // Update or create meta description tag
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', metaDesc);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const path = isLandingView(view) ? (landingViewToPath.get(view) || '/') : '/';
    let fullUrl = `https://vye.onl${path}`;
    if (view === 'landing_blog' && selectedPostId !== null) {
      fullUrl += `?post=${selectedPostId}`;
    }
    canonical.setAttribute('href', fullUrl);

    // Update og:url
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', fullUrl);

  }, [view, selectedPostId, lang]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const nextView = mapPathToView(window.location.pathname);
      setView(nextView);
      if (nextView === 'landing_blog') {
        const params = new URLSearchParams(window.location.search);
        const postVal = params.get('post');
        setSelectedPostId(postVal ? parseInt(postVal, 10) : null);
      } else {
        setSelectedPostId(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Dynamic JSON-LD Article Schema
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const existing = document.getElementById('blog-article-schema');
    if (existing) {
      existing.remove();
    }

    if (view === 'landing_blog' && selectedPostId !== null) {
      const post = blogPosts.find(p => p.id === selectedPostId);
      if (post) {
        const publishDates = ["2026-06-12", "2026-06-08", "2026-06-03", "2026-05-28", "2026-05-22", "2026-05-15"];
        const publishDate = publishDates[post.id - 1] || "2026-05-15";
        
        const schema = {
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": lang === 'ar' ? post.titleAr : post.titleEn,
          "description": lang === 'ar' ? post.summaryAr : post.summaryEn,
          "datePublished": publishDate,
          "author": {
            "@type": "Person",
            "name": lang === 'ar' ? post.authorAr : post.authorEn
          },
          "publisher": {
            "@type": "Organization",
            "name": "VYE Technology Solutions",
            "logo": {
              "@type": "ImageObject",
              "url": "https://vye.onl/logo.webp"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://vye.onl/blog/${selectedPostId}`
          }
        };

        const script = document.createElement('script');
        script.id = 'blog-article-schema';
        script.type = 'application/ld+json';
        script.innerHTML = JSON.stringify(schema);
        document.head.appendChild(script);
      }
    }
  }, [view, selectedPostId, lang]);

  const closeMobileNav = () => {
    setIsNavExpanded(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobileNav();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = () => {
      if (window.matchMedia('(min-width: 992px)').matches) {
        closeMobileNav();
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.add('js');
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (!isNavExpanded) return;
      const target = event.target as HTMLElement;
      const clickedOutsideCollapse = navCollapseRef.current && !navCollapseRef.current.contains(target);
      const clickedOutsideToggler = !target.closest('.navbar-toggler');

      if (clickedOutsideCollapse && clickedOutsideToggler) {
        closeMobileNav();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNavExpanded]);

  const navigateToLanding = (targetView: LandingView, event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event?.preventDefault();
    setView(targetView);
    closeMobileNav();
    if (targetView !== 'landing_blog') {
      setSelectedPostId(null);
    }

    if (typeof window !== 'undefined') {
      window.history.pushState({ view: targetView }, '', getPathForView(targetView));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  return (
    <ErrorBoundary>
      <SmoothScroll>
      <MotionConfig reducedMotion="user">
      <div className="saas-portal full-width-view">
      {/* Skip navigation link for keyboard/screen reader users */}
      <a href="#main-content" className="skip-to-main">
        {lang === 'ar' ? 'تخطى إلى المحتوى الرئيسي' : 'Skip to main content'}
      </a>

      {/* Header bar - Hidden on Admin Host */}
      {!isAdminHost && (
        <header
          className={`saas-header navbar navbar-expand-lg fixed-top transition-all header-scrolled ${
            scrolled
              ? 'glass-panel py-2 shadow-sm'
              : 'bg-transparent py-3'
          } ${isNavExpanded ? 'nav-expanded' : ''}`}
          style={{ zIndex: 1050 }}
        >
          <div className="saas-header-inner container w-100 px-4 d-flex align-align-items-center justify-content-between">
            <a className="saas-logo navbar-brand" href="/" onClick={(event) => navigateToLanding('landing', event)} aria-label="VYE home">
              <img className="brand-logo" src="/logo.webp?v=3" alt="VYE Technology Solutions" width="128" height="38" />
            </a>
            <button
              className={`navbar-toggler d-lg-none ${isNavExpanded ? 'expanded' : ''}`}
              type="button"
              onClick={() => setIsNavExpanded(!isNavExpanded)}
              aria-controls="saasNavbar"
              aria-expanded={isNavExpanded}
              aria-label={lang === 'ar' ? 'فتح القائمة' : 'Toggle navigation'}
            >
              <span className="toggler-icon-bar top-bar" />
              <span className="toggler-icon-bar middle-bar" />
              <span className="toggler-icon-bar bottom-bar" />
            </button>
             <div className={`navbar-collapse ${isNavExpanded ? 'show' : ''}`} id="saasNavbar" ref={navCollapseRef}>
              <ul className="saas-nav navbar-nav align-items-lg-center">
                <li className="nav-item">
                  <a href="/" onClick={(event) => navigateToLanding('landing', event)} className={`nav-link${view === 'landing' ? ' active' : ''}`} aria-current={view === 'landing' ? 'page' : undefined}><span>{t('nav.home')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/expertise" onClick={(event) => navigateToLanding('landing_features', event)} className={`nav-link${view === 'landing_features' ? ' active' : ''}`} aria-current={view === 'landing_features' ? 'page' : undefined}><span>{t('nav.expertise')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/engagement" onClick={(event) => navigateToLanding('landing_pricing', event)} className={`nav-link${view === 'landing_pricing' ? ' active' : ''}`} aria-current={view === 'landing_pricing' ? 'page' : undefined}><span>{t('nav.engagement')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/case-studies" onClick={(event) => navigateToLanding('landing_case_studies', event)} className={`nav-link${view === 'landing_case_studies' ? ' active' : ''}`} aria-current={view === 'landing_case_studies' ? 'page' : undefined}><span>{t('nav.caseStudies')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/blog" onClick={(event) => navigateToLanding('landing_blog', event)} className={`nav-link${view === 'landing_blog' ? ' active' : ''}`} aria-current={view === 'landing_blog' ? 'page' : undefined}><span>{t('nav.blog')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/#platforms" onClick={(event) => { event.preventDefault(); navigateToLanding('landing', event); setTimeout(() => document.getElementById('platforms')?.scrollIntoView({behavior: 'smooth'}), 100); }} className="nav-link"><span>{lang === 'ar' ? 'منصاتنا' : 'Platforms'}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/#services" onClick={(event) => { event.preventDefault(); navigateToLanding('landing', event); setTimeout(() => document.getElementById('services')?.scrollIntoView({behavior: 'smooth'}), 100); }} className="nav-link"><span>{t('nav.itServices')}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/#testimonials" onClick={(event) => { event.preventDefault(); navigateToLanding('landing', event); setTimeout(() => document.getElementById('testimonials')?.scrollIntoView({behavior: 'smooth'}), 100); }} className="nav-link"><span>{lang === 'ar' ? 'آراء العملاء' : 'Testimonials'}</span></a>
                </li>
                <li className="nav-item">
                  <a href="/contact" onClick={(event) => navigateToLanding('landing_contact', event)} className={`nav-link${view === 'landing_contact' ? ' active' : ''}`} aria-current={view === 'landing_contact' ? 'page' : undefined}><span>{t('nav.contact')}</span></a>
                </li>
              </ul>
              <div className="nav-actions">
                <a
                  href="/contact"
                  onClick={(event) => navigateToLanding('landing_contact', event)}
                  className="nav-btn nav-btn-primary"
                  style={{ textDecoration: 'none' }}
                >
                  <span>{lang === 'ar' ? 'احجز استشارة' : 'Book a Consultation'}</span>
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setLang(lang === 'ar' ? 'en' : 'ar');
                    closeMobileNav();
                  }}
                  className="lang-toggle"
                  aria-label={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
                  title={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', display: 'inline', verticalAlign: 'middle', marginInlineEnd: '4px' }}><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                  <span>{lang === 'ar' ? 'EN' : 'ع'}</span>
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main SaaS Workspace */}
      <main className="saas-main" id="main-content">
        <AnimatePresence mode="wait">
          <Suspense fallback={<div className="min-vh-100 d-flex justify-content-center align-items-center"><div className="spinner-border text-primary" role="status"></div></div>}>
        {view === 'landing' && (
          <Home t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Features View (VYE Expertise) */}
        {view === 'landing_features' && (
          <Expertise t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Pricing View (VYE Engagement Models) */}
        {view === 'landing_pricing' && (
          <Engagement t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* IT Services & Solutions View */}
        {view === 'landing_it_services' && (
          <ITServices t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}


        {/* Contact View */}
        {view === 'landing_contact' && (
          <Contact t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Case Studies View */}
        {view === 'landing_case_studies' && (
          <CaseStudies t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Solutions View */}
        {view === 'landing_solutions' && (
          <Solutions t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Matjaar View */}
        {view === 'landing_matjaar' && (
          <Matjaar t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}

        {/* Blog View */}
        {view === 'landing_blog' && (
          <Blog t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} selectedPostId={selectedPostId} setSelectedPostId={setSelectedPostId} />
        )}
        {view === 'not_found' && (
          <NotFound t={t as any} lang={lang} navigateToLanding={navigateToLanding} blogPosts={blogPosts} />
        )}
                </Suspense>
        </AnimatePresence>
      </main>
      
      {view.startsWith('landing') && view !== 'landing_contact' && (
        <section className="landing-section section-dark text-center" style={{ padding: '6rem 2rem' }}>
          <div className="section-inner cta-banner-inner max-w-4xl mx-auto">
            <h2 className="fs-2 fs-md-1 fw-black text-white mb-4" style={{ lineHeight: '1.4' }}>
              {lang === 'ar' ? 'ابدأ رحلة التحول الرقمي الآن' : 'Start your digital transformation journey now'}
            </h2>
            <div className="cta-banner-buttons mt-4 d-flex justify-content-center">
              <motion.a 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href="/contact" 
                onClick={(event) => navigateToLanding('landing_contact', event)} 
                className="cta-btn primary-cta shadow-[0_10px_20px_rgba(0,180,216,0.2)]"
              >
                {lang === 'ar' ? 'اتصل بنا' : 'Contact Us'}
              </motion.a>
            </div>
          </div>
        </section>
      )}
      
      {view.startsWith('landing') && (
        <footer className="saas-footer">
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[--vye-cyan]/30 to-transparent" />
          
          <div className="section-inner footer-inner container px-4">
            <div className="col-12 col-md-3 footer-columns row g-5">
              <div className="col-12 col-md-3 footer-column-brand d-flex flex-column gap-3">
                <img className="brand-logo" src="/logo-light.webp?v=4" alt="VYE Technology Solutions" width="121" height="36" loading="lazy" />
                <p className="footer-brand-desc small text-white-50 lh-lg">
                  {lang === 'ar' ? 'شريكك الاستراتيجي للابتكار والتحول الرقمي في مصر.' : 'Your strategic partner for innovation and digital transformation in Egypt.'}
                </p>
                <div className="footer-social-links">
                  {[
                    { icon: (
                      <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
                    ), href: 'https://www.linkedin.com/company/vye-tech', label: 'LinkedIn' },
                    { icon: (
                      <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24"><path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.05c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.52 8.52 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/></svg>
                    ), href: 'https://twitter.com/vye_tech', label: 'Twitter' },
                    { icon: (
                      <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a5.8 5.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.81 11.81 0 0 0 12.052 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413Z"/></svg>
                    ), href: t('contact.whatsapp'), label: 'WhatsApp' }
                  ].map(({ icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="footer-social-link"
                    >
                      {icon}
                    </a>
                  ))}
                </div>
              </div>
              <div className="col-12 col-md-3 footer-column-links d-flex flex-column gap-3 text-start">
                <h3 className="h4 small fw-bold uppercase tracking-wider text-white">{lang === 'ar' ? 'الشركة' : 'Company'}</h3>
                <ul className="d-flex flex-column gap-2">
                  <li><a href="/" onClick={(event) => navigateToLanding('landing', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.home')}</a></li>
                  <li><a href="/expertise" onClick={(event) => navigateToLanding('landing_features', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.expertise')}</a></li>
                  <li><a href="/engagement" onClick={(event) => navigateToLanding('landing_pricing', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.engagement')}</a></li>
                  <li><a href="/case-studies" onClick={(event) => navigateToLanding('landing_case_studies', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.caseStudies')}</a></li>
                  <li><a href="/blog" onClick={(event) => navigateToLanding('landing_blog', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.blog')}</a></li>
                  <li><a href="/resources/guides/ux-best-practices" className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">UX Best Practices</a></li>
                </ul>
              </div>
              <div className="col-12 col-md-3 footer-column-links d-flex flex-column gap-3 text-start">
                <h3 className="h4 small fw-bold uppercase tracking-wider text-white">{lang === 'ar' ? 'الخدمات' : 'Solutions'}</h3>
                <ul className="d-flex flex-column gap-2">
                  <li><a href="/it-services" onClick={(event) => navigateToLanding('landing_it_services', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.itServices')}</a></li>
                  <li><a href="/contact" onClick={(event) => navigateToLanding('landing_contact', event)} className="small text-gray-300 hover:text-[--vye-cyan] transition-colors">{t('nav.contact')}</a></li>
                </ul>
              </div>
              <div className="col-12 col-md-3 footer-column-links d-flex flex-column gap-3 text-start">
                <h3 className="h4 small fw-bold uppercase tracking-wider text-white">{t('footer.newsletter')}</h3>
                <div className="newsletter-form">
                  {newsletterStatus === 'success' ? (
                    <div style={{ padding: '12px', background: 'rgba(37,211,102,0.1)', color: '#25D366', borderRadius: '8px', fontSize: '0.875rem', marginTop: '8px' }}>
                      {lang === 'ar' ? 'تم الاشتراك بنجاح!' : 'Successfully subscribed!'}
                    </div>
                  ) : (
                    <form onSubmit={(e) => { e.preventDefault(); setNewsletterStatus('success'); }} style={{ display: 'flex', gap: '8px', width: '100%' }}>
                      <input
                        type="email"
                        required
                        placeholder={t('footer.newsletterPlaceholder')}
                      />
                      <button type="submit">
                        {t('footer.newsletterCta')}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
            
            <div className="footer-bottom-bar border-t border-white-5 pt-4 mt-5 d-flex flex-column flex-md-row justify-content-between align-align-items-center gap-3 small text-white-50">
              <div>
                © {new Date().getFullYear()} VYE Technology Solutions Egypt. {t('footer.rights')}
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
    <a
      href={t('contact.whatsapp')}
      target="_blank"
      rel="noopener noreferrer"
      className="floating-whatsapp-btn"
      aria-label="Chat on WhatsApp"
      style={{
        position: 'fixed',
        insetInlineEnd: '24px',
        bottom: '96px',
        backgroundColor: '#25D366',
        color: 'white',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.35)',
        zIndex: 1000,
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '32px', height: '32px' }}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a5.8 5.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.82 9.82 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.81 11.81 0 0 0 12.052 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.88 11.88 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413Z"/>
      </svg>
    </a>
    <AntigravityLauncher />
    {!cookieConsent && (
      <div style={{ position: 'fixed', bottom: '20px', left: '20px', right: '20px', backgroundColor: '#121212', color: '#fff', padding: '16px', borderRadius: '12px', zIndex: 99999, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
        <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, color: '#ccc' }}>
          {lang === 'ar' ? 'نحن نستخدم ملفات تعريف الارتباط لتحسين تجربتك. بالاستمرار في تصفح الموقع، فإنك توافق على سياسة الخصوصية الخاصة بنا.' : 'We use cookies to improve your experience. By continuing to browse, you agree to our privacy policy.'}
        </p>
        <button onClick={acceptCookies} style={{ marginLeft: '16px', marginRight: lang === 'ar' ? '16px' : '0', padding: '8px 16px', backgroundColor: 'var(--vye-cyan)', color: '#000', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          {lang === 'ar' ? 'موافق' : 'Accept'}
        </button>
      </div>
    )}
    </MotionConfig>
    </SmoothScroll>
    </ErrorBoundary>
  );
}
